'use client'

import { Loader2, RefreshCw } from 'lucide-react'
import { GRADE_COLORS, TOOL_GROUPS } from '@/lib/radar/scoring'
import { hasUsableScore } from '@/lib/radar/tool-scores'
import { TOOLS } from '@/types/radar'
import type { ToolKey, ToolState } from '@/types/radar'
import { responseCoverageForResult } from '@/lib/tools/response-coverage'
import { GradeChip, InstrumentLabel, Panel } from './ui'

/**
 * Per-tool results, grouped by the LLM-infrastructure / citation-monitoring
 * split that Radar's positioning rests on.
 *
 * Score and grade are read through `hasUsableScore`, so a tool that withheld
 * shows "--" with no grade chip and no colour — the same refusal the headline
 * respects. Every row keeps its Details and Re-run affordances, including
 * failed rows, which are the ones most likely to need a re-run.
 *
 * The grid collapses to a stacked two-column layout below `md`; the header row
 * is hidden there since each cell carries its own label.
 */

/** Header grid. Kept in sync by hand with the row grid in ToolRow. */
const HEADER_COLS = 'md:grid-cols-[1fr_84px_72px_72px_140px]'

export function ToolResultsTable({
  tools,
  visibleKeys,
  filterLabel,
  onClearFilter,
  onToolClick,
  onRetry,
}: {
  tools: Record<ToolKey, ToolState>
  /**
   * Tools to render. Filtering is the caller's job — DashboardOverview owns
   * the (persisted) filter state, so this component stays presentational and
   * there is exactly one place a tool can be hidden from.
   */
  visibleKeys?: Set<ToolKey>
  /** Human description of the active filter, e.g. "Crawlability · 3 tools". */
  filterLabel?: string | null
  onClearFilter?: () => void
  onToolClick: (key: ToolKey) => void
  onRetry: (key: ToolKey) => void
}) {
  const visible = visibleKeys
    ? TOOLS.filter(t => visibleKeys.has(t.key))
    : TOOLS

  return (
    // Named region: the page has several stacked panels, and a screen-reader
    // user landing mid-list needs to know which one they are in.
    <Panel aria-label='Tool results'>
      {filterLabel && (
        <div className='flex items-center justify-between gap-3 border-b border-hairline px-6 py-3 sm:px-7'>
          <InstrumentLabel tone='secondary'>
            Filtered · {filterLabel}
          </InstrumentLabel>
          {onClearFilter && (
            <button
              type='button'
              onClick={onClearFilter}
              className='text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
            >
              Show all tools
            </button>
          )}
        </div>
      )}

      {visible.length === 0 && (
        <span className='block px-6 py-8 text-center text-[13px] text-instrument sm:px-7'>
          No tools match the current filter.
        </span>
      )}

      <div
        className={`hidden gap-3 border-b border-hairline-strong px-6 py-3 sm:px-7 md:grid ${HEADER_COLS}`}
      >
        <span className='instrument-label text-instrument'>Tool</span>
        <span className='instrument-label text-instrument text-right'>
          Score
        </span>
        <span className='instrument-label text-instrument text-center'>
          Grade
        </span>
        <span className='instrument-label text-instrument text-right'>
          Time
        </span>
        <span className='instrument-label text-instrument text-right'>
          Action
        </span>
      </div>

      {Object.values(TOOL_GROUPS).map(group => {
        const groupTools = visible.filter(t =>
          (group.keys as readonly string[]).includes(t.key)
        )
        if (groupTools.length === 0) return null

        return (
          <div key={group.label}>
            <div className='flex items-center gap-2 px-6 pb-1.5 pt-3 sm:px-7'>
              <span
                aria-hidden='true'
                className='inline-block h-3 w-[3px] shrink-0'
                style={{ background: group.color }}
              />
              <span
                className='instrument-label leading-none'
                style={{ color: group.color }}
              >
                {group.label}
              </span>
            </div>

            {groupTools.map(t => (
              <ToolRow
                key={t.key}
                toolKey={t.key}
                name={t.name}
                estSeconds={t.estSeconds}
                state={tools[t.key]}
                onToolClick={onToolClick}
                onRetry={onRetry}
              />
            ))}
          </div>
        )
      })}
    </Panel>
  )
}

function ToolRow({
  toolKey,
  name,
  estSeconds,
  state,
  onToolClick,
  onRetry,
}: {
  toolKey: ToolKey
  name: string
  estSeconds: number
  state: ToolState
  onToolClick: (key: ToolKey) => void
  onRetry: (key: ToolKey) => void
}) {
  const isDone = state.status === 'done'
  const isError = state.status === 'error'
  const scorable = hasUsableScore(state)
  const score = scorable ? (state.data.overallScore as number) : null
  const grade = scorable ? (state.data?.grade as string | undefined) : undefined
  const responseCoverage = state.data
    ? responseCoverageForResult(state.data)
    : null
  const degradedResponseLabel =
    responseCoverage?.status === 'degraded' &&
    typeof responseCoverage.usableResponses === 'number' &&
    typeof responseCoverage.expectedResponses === 'number'
      ? `${responseCoverage.usableResponses}/${responseCoverage.expectedResponses} responses · grade withheld`
      : 'grade withheld'
  const elapsed =
    isDone && state.startedAt && state.finishedAt
      ? `${((state.finishedAt - state.startedAt) / 1000).toFixed(1)}s`
      : null

  return (
    <div className='grid grid-cols-2 items-center gap-x-3 gap-y-1 border-b border-hairline px-6 py-3 text-sm transition-colors last:border-b-0 hover:bg-surface-raised sm:px-7 md:grid-cols-[1fr_84px_72px_72px_140px] md:gap-3'>
      {/* Tool */}
      <div className='col-span-2 flex min-w-0 items-center gap-2 md:col-span-1'>
        <span
          className={isDone ? 'truncate' : 'truncate text-muted-foreground'}
        >
          {name}
        </span>
        {state.status === 'running' && (
          <>
            <Loader2 size={12} className='shrink-0 animate-spin text-primary' />
            <span className='shrink-0 font-mono text-2xs tabular-nums text-instrument'>
              ~{estSeconds}s
            </span>
          </>
        )}
        {state.status === 'queued' && (
          <span className='shrink-0 text-2xs text-instrument'>· queued</span>
        )}
        {state.status === 'locked' && (
          <span className='shrink-0 text-2xs text-instrument'>· paid plan</span>
        )}
        {isError && (
          <span
            className='min-w-0 truncate text-2xs'
            style={{ color: 'var(--cta)' }}
            title={state.error || 'Failed'}
          >
            · {state.error || 'Failed'}
          </span>
        )}
        {isDone && !scorable && (
          <span className='shrink-0 text-2xs text-instrument'>
            · {degradedResponseLabel}
          </span>
        )}
      </div>

      {/* Score */}
      <span
        className='font-mono text-sm font-semibold tabular-nums md:text-right'
        style={{
          color: score !== null ? GRADE_COLORS[grade!] : 'var(--pm-text-muted)',
        }}
      >
        <span className='instrument-label mr-2 text-instrument md:hidden'>
          Score
        </span>
        {score !== null ? score : '--'}
      </span>

      {/* Grade */}
      <span className='md:text-center'>
        <GradeChip grade={grade} />
      </span>

      {/* Time */}
      <span className='font-mono text-xs tabular-nums text-instrument md:text-right'>
        {elapsed || '--'}
      </span>

      {/* Action */}
      <span className='col-span-2 flex items-center gap-2 text-[13px] md:col-span-1 md:justify-end'>
        {isDone && (
          <button
            type='button'
            onClick={() => onToolClick(toolKey)}
            className='text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
          >
            Details
          </button>
        )}
        {(isDone || isError) && (
          <>
            {isDone && (
              <span aria-hidden='true' className='text-instrument'>
                ·
              </span>
            )}
            <button
              type='button'
              onClick={() => onRetry(toolKey)}
              title={`Re-run ${name}`}
              className='inline-flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
            >
              <RefreshCw size={12} aria-hidden='true' />
              Re-run
            </button>
          </>
        )}
      </span>
    </div>
  )
}
